/**
 * IntegrationLogsPanel — Integration Hub
 * -----------------------------------------------------------------------------
 * Aba "Logs" do conector: visão técnica de TODAS as chamadas feitas ao
 * aplicativo (consultas, testes de conexão, verificações de saúde e erros).
 *
 * Nunca exibe token, documento completo ou conteúdo de PDF.
 * Acesso restrito a administradores do tenant (revalidado no banco).
 *
 * @version 1.1.0
 * RELEASE: 2026-08-29 v1.1.0 - Identifica a recuperação idempotente do
 *          resultado/PDF após o acompanhamento de consultas assíncronas.
 * RELEASE: 2026-08-29 v1.0.0 - Criação inicial.
 */

import { useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useIntegrationRequestLogs } from '@/hooks/integrations/useIntegrationRequestLogs';

/** Rótulos amigáveis das ações do gateway */
const ACTION_LABELS: Record<string, string> = {
  execute: 'Consulta',
  test_connection: 'Teste de conexão',
  health_check: 'Verificação de saúde',
  fetch_page: 'Importação (página)',
  poll: 'Acompanhamento',
  result_recovery: 'Recuperação do resultado',
};

const PERIODS = [
  { value: '1', label: 'Últimas 24 horas' },
  { value: '7', label: 'Últimos 7 dias' },
  { value: '30', label: 'Últimos 30 dias' },
];

interface IntegrationLogsPanelProps {
  providerSlug: string;
}

export function IntegrationLogsPanel({ providerSlug }: IntegrationLogsPanelProps) {
  const [period, setPeriod] = useState('7');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);

  const dateFrom = useMemo(
    () => new Date(Date.now() - Number(period) * 24 * 60 * 60 * 1000).toISOString(),
    [period],
  );

  const { items, total, pageSize, isLoading, isFetching, refetch } = useIntegrationRequestLogs({
    providerSlug,
    status: status === 'all' ? null : status,
    dateFrom,
    page,
  });

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      {/* ─────────── Filtros ─────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={period}
          onValueChange={(value) => {
            setPeriod(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-8 w-[170px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={status}
          onValueChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-8 w-[140px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="success">Sucesso</SelectItem>
            <SelectItem value="error">Erro</SelectItem>
          </SelectContent>
        </Select>

        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-2 text-xs"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* ─────────── Tabela ─────────── */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Data</TableHead>
              <TableHead className="text-xs">Ação</TableHead>
              <TableHead className="text-xs">Recurso</TableHead>
              <TableHead className="text-xs">Conexão</TableHead>
              <TableHead className="text-xs">Status</TableHead>
              <TableHead className="text-xs">HTTP</TableHead>
              <TableHead className="text-xs">Duração</TableHead>
              <TableHead className="text-xs">Detalhe</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                  Carregando...
                </TableCell>
              </TableRow>
            )}

            {!isLoading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
                  Nenhuma chamada registrada no período.
                </TableCell>
              </TableRow>
            )}

            {items.map((log) => (
              <TableRow key={log.id}>
                <TableCell className="text-xs">
                  {new Date(log.created_at).toLocaleString('pt-BR')}
                </TableCell>
                <TableCell className="text-xs">
                  {ACTION_LABELS[log.action] ?? log.action}
                </TableCell>
                <TableCell className="text-xs">{log.operation_code ?? '—'}</TableCell>
                <TableCell className="text-xs">{log.connection_name ?? '—'}</TableCell>
                <TableCell>
                  <Badge variant={log.status === 'success' ? 'secondary' : 'destructive'}>
                    {log.status === 'success' ? 'OK' : 'Erro'}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs">{log.http_status ?? '—'}</TableCell>
                <TableCell className="text-xs">
                  {log.duration_ms ? `${log.duration_ms} ms` : '—'}
                </TableCell>
                <TableCell className="max-w-[280px] truncate text-xs text-muted-foreground">
                  {log.error_message ?? log.request_id ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* ─────────── Paginação ─────────── */}
      {total > pageSize && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {total} registro(s) · página {page} de {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
